import axios from "axios";

export enum EncounterType {
    EMAIL_OUTGOING = "EMAIL_OUTGOING",
    EMAIL_INCOMING = "EMAIL_INCOMING",
    FAX = "FAX",
    TELEPHONE = "TELEPHONE",
    FACE_TO_FACE = "FACE_TO_FACE",
    VOICE_MEMO = "VOICE_MEMO",
    TASK = "TASK",
    APPOINTMENT = "APPOINTMENT",
    POST = "POST",

    TODO = "TODO"
}

export interface Encounter {
    id: string | null;
    title: string | null;
    description: string | null;
    encounterType: EncounterType;
    upstreamId: string | null;
    duplicate: boolean;
    originalKnownSince: Date;
    contactRef: string | null;
    updatedBy: string | null;
    createdBy: string | null;
    contentMailingsDdj: boolean;
    contentMailingsSeedsMagazine: boolean;
    contentMailingsPostcard: boolean;
    furtherEmailAddressesToAnswer: string[];
    assigneeRef?: string | null;
}

interface EncounterApi {
    search(
        contactId: string,
        searchTerm: string,
        start: string,
        end: string,
        encounterType: string
    ): Promise<Encounter[]>;

    list(contactId: string): Promise<Encounter[]>;

    listByEncounterType(contactId: string, encounterType: EncounterType): Promise<Encounter[]>;

    getById(id: string): Promise<Encounter>;

    getMinDate(id: string): Promise<Date | null>;

    getMaxDate(id: string): Promise<Date | null>;

    add(contactId: string, encounter: Encounter): Promise<string>;

    edit(
        contactId: string,
        id: string,
        encounter: Encounter
    ): Promise<void>;

    uploadContactsAndCreateEncounter(file: File,  encounter: Encounter): Promise<void>

    move(
        contactId: string,
        encounter: Encounter,
        newContactId: string
    ): Promise<void>;

    delete(contactId: string, id: string): Promise<void>;

    addVoiceMemo(contactId: string, record: Blob): Promise<void>;

    transcribeAgain(encounter: Encounter): Promise<void>;

    generateDownloadLink(contactId: string, id: string): string;

    count(contactId: string): Promise<number>;
}

export const encounterApi: EncounterApi = {
    async list(contactId) {
        const result = (await axios.get(`/api/contacts/${contactId}/encounters`))
            .data as Encounter[];
        if (result) {
            return result.map(
                (v) =>
                    ({
                        ...v,
                        originalKnownSince: v.originalKnownSince
                            ? new Date(v.originalKnownSince)
                            : null,
                    } as Encounter)
            );
        }
        return [];
    },
    async listByEncounterType(contactId, encounterType) {
        const result = (await axios.get(`/api/contacts/${contactId}/encounters/types/${encounterType}`))
            .data as Encounter[];
        if (result) {
            return result.map(
                (v) =>
                    ({
                        ...v,
                        originalKnownSince: v.originalKnownSince
                            ? new Date(v.originalKnownSince)
                            : null,
                    } as Encounter)
            );
        }
        return [];
    },
    async search(contactId, searchTerm, start, end, encounterType) {
        if (!searchTerm) {
            searchTerm = "";
        }
        if (!encounterType) {
            encounterType = "";
        }
        const result = (
            await axios.get(
                `/api/contacts/${contactId}/encounters/search?searchTerm=${searchTerm}&start=${start}&end=${end}&encounterType=${encounterType}`
            )
        ).data as Encounter[];
        if (result) {
            return result.map(
                (v) =>
                    ({
                        ...v,
                        originalKnownSince: v.originalKnownSince
                            ? new Date(v.originalKnownSince)
                            : null,
                    } as Encounter)
            );
        }
        return [];
    },
    async getMinDate(contactId) {
        const result = (await axios.get(`/api/contacts/${contactId}/encounters/min`)).data;
        return result ? new Date(result) : null;
    },
    async getMaxDate(contactId) {
        const result = (await axios.get(`/api/contacts/${contactId}/encounters/max`)).data;
        return result ? new Date(result) : null;
    },
    async edit(contactId, id, encounter) {
        encounter.contactRef = contactId;
        return await axios.put(
            `/api/contacts/${contactId}/encounters/${id}`,
            encounter
        );
    },

    async move(contactId, encounter, newContactId) {
        encounter.contactRef = contactId;
        return await axios.put(
            `/api/contacts/${contactId}/encounters/${encounter.id}/move/${newContactId}`,
            encounter
        );
    },

    async getById(id) {
        const result = (await axios.get(`/api/contacts/encounters/${id}`)).data;
        return {
            ...result,
            createdAtUtc: result.createdAtUtc ? new Date(result.createdAtUtc) : null,
        };
    },

    async add(contactId, contactHistoryEntry) {
        return (
            await axios.post(
                `/api/contacts/${contactId}/encounters`,
                contactHistoryEntry
            )
        ).data;
    },

   async uploadContactsAndCreateEncounter(file, encounter) {
     const formData = new FormData();
     formData.append("file", file);
     formData.append("encounterType", encounter.encounterType);
     formData.append("originalKnownSince", encounter.originalKnownSince.toISOString());
     formData.append("description", encounter.description ?? "");
     return (
       await axios.post(
         `/api/contacts/encounters/upload-with-contacts`,
         formData
       )
     )
   },

    async delete(contactId, id) {
        return await axios.delete(`/api/contacts/${contactId}/encounters/${id}`);
    },

    async addVoiceMemo(contactId, record) {
        const formData = new FormData();
        formData.append("audio", record, "voicememo.mp3");
        await axios.post(
            `/api/contacts/${contactId}/encounters/voicememo`,
            formData
        );
    },

    async transcribeAgain(encounter) {
        await axios.put(
            `/api/contacts/${encounter.contactRef}/encounters/${encounter.id}/voicememo/transcribe`
        );
    },

    async count(contactId) {
        return (await axios.get(
            `/api/contacts/${contactId}/encounters/_count`
        )).data;
    },

    generateDownloadLink(contactId: string, id: string): string {
        return `/api/contacts/${contactId}/encounters/${id}/voicememo`;
    },

};
